import React, { useState, useCallback, useEffect } from 'react';
import { Modal } from '../UI/Modal';
import { Input } from '../UI/Input';
import { Button } from '../UI/Button';
import type { OrderDetailResponse, OrderReplaceRequest } from '../../types/api';
import { formatSide, formatStatus } from '../../utils/orderFormat';

interface AmendOrderModalProps {
  order: OrderDetailResponse;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (changes: OrderReplaceRequest) => Promise<void>;
  isLoading: boolean;
}

interface FormData {
  newQuantity: string;
  newLimitPrice: string;
}

interface FormErrors {
  newQuantity?: string;
  newLimitPrice?: string;
  general?: string;
}

/**
 * Amend Order Modal component
 * Allows modifying order quantity or price
 */
export function AmendOrderModal({
  order,
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}: AmendOrderModalProps) {
  const [formData, setFormData] = useState<FormData>({
    newQuantity: order.quantity,
    newLimitPrice: order.limit_price || '',
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // Reset form when order changes
  useEffect(() => {
    setFormData({
      newQuantity: order.quantity,
      newLimitPrice: order.limit_price || '',
    });
    setErrors({});
  }, [order.id, order.quantity, order.limit_price]);

  const filledQty = parseFloat(order.filled_quantity);

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    // Quantity validation
    const newQty = parseFloat(formData.newQuantity);
    if (isNaN(newQty) || newQty <= 0) {
      newErrors.newQuantity = 'Quantity must be a positive number';
    } else if (newQty < filledQty) {
      newErrors.newQuantity = `Cannot reduce below filled quantity (${order.filled_quantity})`;
    }

    // Limit price validation (only for limit orders)
    if (order.order_type === 'limit') {
      const newPrice = parseFloat(formData.newLimitPrice);
      if (isNaN(newPrice) || newPrice <= 0) {
        newErrors.newLimitPrice = 'Limit price must be a positive number';
      }
    }

    // Check if at least one field changed
    const qtyChanged = formData.newQuantity !== order.quantity;
    const priceChanged = formData.newLimitPrice !== (order.limit_price || '');
    if (!qtyChanged && !priceChanged) {
      newErrors.general = 'At least one field must be changed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, order, filledQty]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      // Clear error for this field when user types
      if (errors[name as keyof FormErrors]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
      // Clear general error too
      if (errors.general) {
        setErrors((prev) => ({ ...prev, general: undefined }));
      }
    },
    [errors]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      const changes: OrderReplaceRequest = {};

      // Only include changed fields
      if (formData.newQuantity !== order.quantity) {
        changes.new_quantity = formData.newQuantity;
      }
      if (formData.newLimitPrice !== (order.limit_price || '')) {
        changes.new_limit_price = formData.newLimitPrice;
      }

      await onSubmit(changes);
    },
    [formData, order, validateForm, onSubmit]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Amend Order">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Original Order Summary */}
        <div className="text-sm text-gray-600">
          <p>
            Original Order:{' '}
            <span className="font-medium text-gray-900">
              {formatSide(order.side)} {order.quantity} {order.symbol} @ ${order.limit_price}
            </span>
          </p>
          <p>
            Status:{' '}
            <span className="font-medium text-gray-900">
              {formatStatus(order.status)} ({order.filled_quantity} filled)
            </span>
          </p>
        </div>

        {/* General Error */}
        {errors.general && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
            {errors.general}
          </div>
        )}

        {/* Modify Values Section */}
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-gray-900 mb-4">Modify Values</h4>

          {/* New Quantity */}
          <div className="mb-4">
            <Input
              label="New Quantity"
              name="newQuantity"
              type="text"
              inputMode="decimal"
              value={formData.newQuantity}
              onChange={handleChange}
              error={errors.newQuantity}
              helperText={`Cannot reduce below filled quantity (${order.filled_quantity})`}
            />
            <p className="mt-1 text-xs text-gray-500">
              Original: {order.quantity}
            </p>
          </div>

          {/* New Limit Price */}
          {order.order_type === 'limit' && (
            <div className="mb-4">
              <Input
                label="New Limit Price"
                name="newLimitPrice"
                type="text"
                inputMode="decimal"
                value={formData.newLimitPrice}
                onChange={handleChange}
                error={errors.newLimitPrice}
                placeholder="150.00"
              />
              <p className="mt-1 text-xs text-gray-500">
                Original: ${order.limit_price}
              </p>
            </div>
          )}
        </div>

        {/* Warning */}
        <div className="flex items-start bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <svg
            className="flex-shrink-0 h-5 w-5 text-yellow-400 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <p className="ml-3 text-sm text-yellow-700">
            Amending will create a new order and cancel the original
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isLoading}>
            Submit Amendment
          </Button>
        </div>
      </form>
    </Modal>
  );
}
